import clsx from 'clsx'
import { type ComponentProps, useCallback, useContext, useEffect, useId, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import AssistChip from 'LuxKit/Chips/AssistChip'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import ModalContext from 'contexts/ModalContext'
import { useIsInModal } from 'contexts/ModalProvider'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { mediaQueryUp } from 'components/utils/breakpoint'
import AccommodationRoomQuantityOptions from './AccommodationRoomQuantityOptions'
import { AccommodationRoomSelectionErrorContext } from './AccommodationRoomSelectionError'
import AccommodationRoomQuantityModal from './AccommodationRoomQuantityModal'

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  min-width: 0;
  width: 100%;
  ${mediaQueryUp.tablet} { gap: ${rem(4)}; }
`

const QuantityChip = styled(AssistChip)`
  min-width: ${rem(52)};
  flex: 1;
  > :not(svg) {
    flex: 1;
    text-align: center;
  }
  &&.variant-filled:not(:disabled) {
    --chip-stroke-color: ${props => props.theme.palette.neutral.default.four};
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
    color: ${props => props.theme.palette.neutral.surface.normalContrast};
    &.selected {
      --chip-stroke-color: ${props => props.theme.palette.highlight.primary.darkForeground};
      background-color: ${props => props.theme.palette.highlight.primary.darkBackground};
      color: ${props => props.theme.palette.highlight.primary.darkContrast};
    }
  }
  &&.variant-filled[aria-invalid='true']:not(:disabled) {
    --chip-stroke-color: ${props => props.theme.palette.messaging.critical.normalForeground};
    background-color: ${props => props.theme.palette.messaging.critical.lightBackground};
  }

  ${mediaQueryUp.tablet} {
    width: ${rem(100)};
    flex: none;
  }
`

const RemoveButton = styled(IconButton)`
  --icon-button-size: ${rem(32)};
`

interface Props extends ComponentProps<typeof AccommodationRoomQuantityOptions> {
  disabled?: boolean
  error?: boolean
  /** Optional initial CTA, used only on mobile while the quantity is zero. */
  initialActionLabel?: string
  /** Feature-owned context for quote changes not represented by the numeric options. */
  quoteKey?: string
}

function AccommodationRoomQuantitySelect(props: Props) {
  const { value, options, label, onChange, disabled, error, initialActionLabel, quoteKey } = props
  const selectionError = useContext(AccommodationRoomSelectionErrorContext)
  const hasError = error || (selectionError && value === 0)
  const intl = useIntl()
  const id = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const modalResolveRef = useRef<((quantity?: number) => void) | undefined>(undefined)
  const [open, setOpen] = useState(false)
  const showModal = useContext(ModalContext)
  const isMobile = useScreenSizeOnly('mobile')
  const isInModal = useIsInModal()
  const usesBottomSheet = isMobile && !isInModal
  const selectionKey = JSON.stringify([quoteKey, value, disabled, usesBottomSheet, options.map(option => [option.quantity, option.price, !!option.disabled])])
  const currentSelectionRef = useRef({ key: selectionKey, onChange })
  const showInitialAction = isMobile && value === 0 && !!initialActionLabel
  const hasZeroOption = options.some(option => option.quantity === 0 && !option.disabled)

  const title = intl.formatMessage({
    id: '3Z8_Haj-hhO53_b1Ncse',
    defaultMessage: 'Select Rooms',
    description: 'Heading of the quantity picker for an accommodation package on the property page',
  })
  const quantityLabel = intl.formatMessage({
    id: 'ahmYShCWyInEu2y92eU2',
    defaultMessage: '{label}: {count, plural, one {# room selected} other {# rooms selected}}',
    description: 'Accessible quantity selector name containing the room and package context and selected room count',
  }, { label, count: value })
  const removeLabel = intl.formatMessage({
    id: 'rMEA2rPj0AZ3hAsuBoLL',
    defaultMessage: 'Remove all selected rooms for {label}',
    description: 'Accessible label for removing the room selections represented by one accommodation package',
  }, { label })
  const initialActionAccessibleLabel = intl.formatMessage({
    id: 'sUj-39_ChxlXRFKlUPig',
    defaultMessage: '{action}: {label}',
    description: 'Accessible initial quantity selection action combining the button text with its room and package context',
  }, { action: initialActionLabel, label })

  const focusTrigger = useCallback(() => {
    triggerRef.current?.focus()
  }, [])

  const closeDropdown = useCallback<NonNullable<ComponentProps<typeof DropdownList>['onClose']>>((_event, reason) => {
    setOpen(false)
    if (reason !== 'outside-click') focusTrigger()
  }, [focusTrigger])

  const handleQuantityChange = useCallback((quantity: number) => {
    currentSelectionRef.current.onChange(quantity)
    setOpen(false)
    // React may replace the initial action with a quantity chip when the parent commits.
    requestAnimationFrame(focusTrigger)
  }, [focusTrigger])

  const handleOpen = useCallback(async() => {
    if (usesBottomSheet) {
      setOpen(true)
      const quantity = await showModal<number | undefined>((resolve) => {
        modalResolveRef.current = resolve
        return <AccommodationRoomQuantityModal
          value={value}
          options={options}
          label={label}
          title={title}
          onChange={resolve}
        />
      })
      modalResolveRef.current = undefined
      setOpen(false)
      if (quantity !== undefined && currentSelectionRef.current.key === selectionKey) {
        handleQuantityChange(quantity)
      } else {
        focusTrigger()
      }
    } else {
      setOpen(current => !current)
    }
  }, [usesBottomSheet, showModal, value, options, label, title, handleQuantityChange, focusTrigger, selectionKey])

  const handleRemove = useCallback(() => {
    handleQuantityChange(0)
  }, [handleQuantityChange])

  useEffect(() => {
    currentSelectionRef.current = { key: selectionKey, onChange }
  }, [selectionKey, onChange])

  useEffect(() => {
    // A modal contains an opening-time quote. Dismiss it if options or eligibility change.
    modalResolveRef.current?.()
    setOpen(false)
  }, [selectionKey])

  useEffect(() => () => {
    modalResolveRef.current?.()
  }, [])

  useEffect(() => {
    if (open) {
      const input = dropdownRef.current?.querySelector<HTMLInputElement>('input:checked:not(:disabled)') ??
        dropdownRef.current?.querySelector<HTMLInputElement>('input:not(:disabled)')
      input?.focus()
    }
  }, [open])

  return <Controls>
    {showInitialAction && <TextButton
      ref={triggerRef}
      kind="primary"
      fit="full-width"
      onClick={handleOpen}
      disabled={disabled}
      aria-label={initialActionAccessibleLabel}
      aria-haspopup={usesBottomSheet ? 'dialog' : undefined}
      aria-expanded={open}
    >
      {initialActionLabel}
    </TextButton>}
    {!showInitialAction && <QuantityChip
      ref={triggerRef}
      size="medium"
      variant="filled"
      className={clsx({ selected: value > 0 })}
      onClick={handleOpen}
      disabled={disabled}
      aria-invalid={hasError || undefined}
      endIcon={<DropdownChevron open={open} />}
      aria-label={quantityLabel}
      aria-haspopup={usesBottomSheet ? 'dialog' : undefined}
      aria-expanded={open}
      aria-controls={open && !usesBottomSheet ? id : undefined}
    >
      {String(value)}
    </QuantityChip>}
    {value > 0 && hasZeroOption && <RemoveButton
      kind="secondary"
      shape="circle"
      size="small"
      aria-label={removeLabel}
      onClick={handleRemove}
      disabled={disabled}
    >
      <LineTrashAltIcon />
    </RemoveButton>}
    {!usesBottomSheet && <DropdownList
      ref={dropdownRef}
      id={id}
      size="S"
      width="auto"
      placement="bottom-end"
      enableFlip
      autoHeight
      open={open}
      anchorRef={triggerRef}
      triggerRef={triggerRef}
      onClose={closeDropdown}
      portaled
    >
      <AccommodationRoomQuantityOptions value={value} options={options} label={label} onChange={handleQuantityChange} />
    </DropdownList>}
  </Controls>
}

export default AccommodationRoomQuantitySelect
